import type { User } from 'types/User.ts';
import type { Document } from '#types/Document.ts';
import type { UserRow } from '#types/User.ts';

export const LEG_STATUSES = ['pending', 'transiting', 'delivered', 'cancelled'] as const;
export type LEG_STATUS = (typeof LEG_STATUSES)[number];

export const LEG_COLORS = ['grey', 'aqua', 'green', 'red'] as const;
export type LEG_COLOR = (typeof LEG_COLORS)[number];

export const LEG_SUPPLIER_REFERENCE_TYPES = ['leg', 'shipment'] as const;
export type LEG_SUPPLIER_REFERENCE_TYPE = (typeof LEG_SUPPLIER_REFERENCE_TYPES)[number];

type Longitude = number;
type Latitude = number;

export type LegRow = {
  id: string;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
  owner_id: string;
  bc_sync_at: string | null;
  bc_sync_pending: boolean;
  logistics_ex_id: string;
  integration_version: number;
  shipment_id: string;
  index: number | null;
  supplier_id: string | null;
  driver_id: string | null;
  origin_address: string;
  origin_postcode: string | null;
  origin_city: string | null;
  origin_country: string | null;
  origin_address_name: string | null;
  origin_position: [Longitude, Latitude];
  origin_tz: string | null;
  origin_expected_datetime: string;
  origin_actual_datetime: string | null;
  origin_expected_from_datetime: string | null;
  origin_waiting_started_at: string | null;
  origin_manual_actual_datetime: boolean;
  origin_is_shipper: boolean;
  poc_required: boolean;
  poc_url: string | null;
  destination_address: string;
  destination_postcode: string | null;
  destination_city: string | null;
  destination_country: string | null;
  destination_address_name: string | null;
  destination_position: [Longitude, Latitude];
  destination_tz: string | null;
  destination_expected_datetime: string;
  destination_actual_datetime: string | null;
  destination_expected_from_datetime: string | null;
  destination_waiting_started_at: string | null;
  destination_manual_actual_datetime: boolean;
  destination_is_consignee: boolean;
  pod_required: boolean;
  pod_url: string | null;
  documents_ids: string[];
  supplier_reference_id: string | null;
  supplier_reference_type: LEG_SUPPLIER_REFERENCE_TYPE;
  external_shipper_reference: string | null;
  status: LEG_STATUS;
  delayed: boolean;
  next_eta: string | null;
  shipment_logistics_ex_id: string | null;
  ordinal: number | null;
  origin_comments: string | null;
  origin_signer_name: string | null;
  origin_contact_name: string | null;
  origin_contact_number: string | null;
  destination_comments: string | null;
  destination_signer_name: string | null;
  destination_contact_name: string | null;
  destination_contact_number: string | null;
  instructions: string | null;
  run_order: number | null;
  trackingmore: boolean;
  trackingmore_sync_pending: boolean;
  hawbs_ids: string[];
  hawbs_trackingmore_ids: string[];
  trackingmore_data: Record<string, string>[] | null;
};

export type LegRowInsert = Partial<Omit<LegRow, 'delayed'>> &
  NonNullable<
    Pick<
      LegRow,
      | 'owner_id'
      | 'shipment_id'
      | 'origin_address'
      | 'origin_expected_datetime'
      | 'poc_required'
      | 'destination_address'
      | 'destination_expected_datetime'
      | 'pod_required'
      | 'ordinal'
    >
  >;

export type LegRowUpdate = Partial<Omit<LegRow, 'id' | 'delayed'>>;

export type Leg = LegRow & {
  supplier: User | UserRow | null;
  driver: User | UserRow | null;
  documents: Document[];
  logistics_export_pending: boolean;
  customer_name: string | null;
};

export type LegBC = {
  id: string;
  exId: string;
  timestamp: number;
  createdAt: number;
  updatedAt: number;
  deletedAt: number;
  originAddress: string;
  originPosition: string;
  destinationAddress: string;
  destinationPosition: string;
  supplierId: string;
  originExpectedDatetime: number;
  destinationExpectedDatetime: number;
};
